import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { ReplaySubject, of } from 'rxjs';
import { takeUntil, filter, catchError } from 'rxjs/operators';
import { UbiIdleService, UbiIdleAuthStates } from 'uber-ui';

import { AuthService } from './auth.service';

import * as AuthActions from 'store/actions/auth.actions';
import * as DomanActions from 'store/actions/domain.actions';
import { AppState } from 'store/root-reducer';

import { environment } from 'environment/environment';

@Injectable({
  providedIn: 'root'
})
export class IdleService {

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject(1);

  constructor(private ubiIdleService: UbiIdleService,
              private store: Store<AppState>,
              private router: Router,
              private authService: AuthService) { }

  /**
   * Dispatch logout when time out
   * @method onTimeout
   */
  private onTimeout(): void {
    this.store.dispatch(new AuthActions.LogOut({ isSessionTimeOut: true }));
  }

  /**
   * Subscribe auth state in uber-ui
   * @method authStateSubscriber
   */
  private authStateSubscriber(): void {
    this.ubiIdleService.getAuthState()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((state: UbiIdleAuthStates) => state !== null)
      )
      .subscribe((state: UbiIdleAuthStates) => {
        if (state === 'signedIn') {
          this.router.navigateByUrl('dashboard');
        } else if (state === 'signedOut') {
          this.store.dispatch(new AuthActions.LogOut({ isSessionTimeOut: false }));
        }
      });
  }

  /**
   * Get current user to check auth state
   * @method getUser
   */
  private getUser(): void {
    this.authService.getUser(true)
      .pipe(
        takeUntil(this.unsubscribe$),
        catchError(() => {
          this.authService.logOut().subscribe();
          this.ubiIdleService.setBrowserLogout();
          this.store.dispatch(new DomanActions.ClearLoadList());
          this.store.dispatch(new AuthActions.ClearGetUser());
          return of(null);
        })
      ).subscribe((user: any) => {
        if (user) {
          this.ubiIdleService.setBrowserLogin();
        }
      });
  }

  /**
   * Start idle service in uber-ui
   * @method start
   */
  start(): void {
    const config: any = {
      sessionTimeout: environment['SESSION_TIMEOUT'] ? environment['SESSION_TIMEOUT'] : 1800000,
      onTimeout: this.onTimeout.bind(this)
    };
    this.ubiIdleService.init(config);
    this.authStateSubscriber();
    this.getUser();
  }

  /**
   * Stop & clear idle service in uber-ui
   * @method stop
   */
  stop(): void {
    this.ubiIdleService.clear();
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
